/**
 * « En classe » : ce que les professeurs ont renseigné, mot pour mot (§6.9).
 *
 * Demandé par Hedi le 6 septembre 2026 : *« dans l'écran des parents, je veux
 * aussi voir ce que les profs ont renseigné sur ce qui a été vu en cours »*.
 *
 * **C'est la phrase, pas la conclusion.** Jusqu'ici cette matière entrait dans
 * le rattachement et dans la lecture de journée, et n'en ressortait que sous
 * forme de compétences : le parent lisait le résultat sans jamais pouvoir lire
 * ce qui l'avait produit. Quand douze devoirs sur douze restent non rattachés,
 * cette phrase est tout ce qui reste.
 *
 * **Le verbatim du professeur n'est pas celui de l'enfant** (règle 4). Ce qui
 * est ici, le parent le lit déjà sur EcoleDirecte avec son propre compte ; la
 * confidentialité protège ce que l'enfant confie au tuteur, pas ce que l'école
 * publie aux familles. Rien de ce que l'enfant écrit le soir n'apparaît sur cet
 * écran.
 *
 * **Une liste vide a deux causes opposées**, et l'écran les sépare : les
 * professeurs n'ont rien écrit, ou le connecteur n'a pas tourné. D'où la date du
 * dernier import, affichée même — surtout — quand il n'y a rien.
 *
 * **Et les images sont là.** Depuis la v0.3.25 le connecteur garde les photos
 * de tableau que les professeurs collent dans leur cahier de textes : elles
 * s'affichent ici, sous URL signée à durée de vie courte (règle 8), jamais en
 * objet public. Les séances importées avant ne portent que le jeton, et l'écran
 * le dit plutôt que de laisser croire à une panne.
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { CahierDesProfs, JourneeEnClasse, RenseignementDuProf } from "@/api/schema";
import { enClair } from "@/ui/dates";
import { etiquette } from "@/ui/matieres";
import { sortDesImages } from "@/ui/imagesDuCahier";
import { bornes, jour, type Periode, type Preselection } from "@/ui/periode";
import { SelecteurDePeriode } from "@/ui/SelecteurDePeriode";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

/** La même semaine que les autres onglets : passer de l'un à l'autre ne doit
 * pas changer de période sous les yeux du parent. */
const DEFAUT: Preselection = "7j";

export function ClasseScreen() {
  const { childId = "" } = useParams();
  const aujourdhui = useMemo(() => new Date(), []);
  const [choix, setChoix] = useState<Preselection>(DEFAUT);
  const [periode, setPeriode] = useState<Periode>(
    () => bornes(DEFAUT, new Date()) ?? { since: "", until: "" },
  );
  const [cahier, setCahier] = useState<CahierDesProfs | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    if (!periode.since || !periode.until) return;
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const charge = await call("cahierDesProfs", {
          params: { child_id: childId },
          query: { since: periode.since, until: periode.until },
        });
        if (!annule) setCahier(charge);
      } catch (echec) {
        if (!annule) {
          setErreur(echec instanceof ApiError ? echec.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      annule = true;
    };
  }, [childId, periode.since, periode.until]);

  const choisir = (valeur: Preselection) => {
    setChoix(valeur);
    const calcule = bornes(valeur, aujourdhui);
    if (calcule) setPeriode(calcule);
  };

  const jours = cahier?.jours ?? [];

  return (
    <Screen
      title="Ce que disent les professeurs"
      lead={
        periode.since && periode.until
          ? `Du ${enClair(periode.since)} au ${enClair(periode.until)}`
          : undefined
      }
    >
      <SelecteurDePeriode
        choix={choix}
        periode={periode}
        max={jour(aujourdhui)}
        onChoisir={choisir}
        onDates={(suivante) => {
          setChoix("libre");
          setPeriode(suivante);
        }}
      />

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!cahier && !erreur ? <Loading /> : null}

      {cahier && jours.length === 0 ? (
        <Card>
          <Empty>
            Rien de renseigné du {enClair(cahier.since)} au {enClair(cahier.until)}.
          </Empty>
          <Synchro cahier={cahier} />
        </Card>
      ) : null}

      {jours.map((journee) => (
        <Journee key={journee.on} journee={journee} />
      ))}

      {cahier && jours.length > 0 ? (
        <Card>
          <Synchro cahier={cahier} />
        </Card>
      ) : null}
    </Screen>
  );
}

/**
 * D'où viennent ces lignes, et de quand.
 *
 * Sans cette phrase, « rien cette semaine » et « le connecteur n'a pas tourné
 * depuis mardi » s'affichent exactement pareil — et la seconde est une panne
 * qu'il faut voir, pas une semaine calme.
 */
function Synchro({ cahier }: { cahier: CahierDesProfs }) {
  if (!cahier.derniere_synchro) {
    return (
      <p className="muted small">
        Aucune donnée reçue d'EcoleDirecte sur cette période. Ce n'est pas
        forcément un cahier vide : ce peut être la synchronisation qui n'a rien
        rapporté.
      </p>
    );
  }
  return (
    <p className="muted small">
      Reçu d'EcoleDirecte, dernière mise à jour le{" "}
      {enClair(cahier.derniere_synchro)}.
    </p>
  );
}

function Journee({ journee }: { journee: JourneeEnClasse }) {
  const lignes = journee.lignes ?? [];
  return (
    <Card title={enClair(journee.on)}>
      {lignes.length === 0 ? (
        <Empty>Rien de renseigné ce jour-là.</Empty>
      ) : (
        <ul className="classe__liste">
          {lignes.map((ligne, index) => (
            <Ligne key={`${ligne.kind}-${ligne.subject}-${index}`} ligne={ligne} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function Ligne({ ligne }: { ligne: RenseignementDuProf }) {
  const sort = sortDesImages(ligne.content ?? "", ligne.images);
  const photographie = sort.forme === "montrees" && sort.seules > 0;
  return (
    <li className="classe__ligne">
      <p className="classe__entete">
        <span className="chip" data-on={ligne.kind === "seance" ? "oui" : "non"}>
          {ligne.kind === "seance" ? "vu en cours" : "à faire"}
        </span>{" "}
        <strong>{etiquette(ligne.subject)}</strong>
        {ligne.due_on ? (
          <span className="muted small"> · pour le {enClair(ligne.due_on)}</span>
        ) : null}
      </p>
      {/* Le texte du professeur, tel qu'il l'a écrit. Aucune reformulation :
          une phrase reformulée n'est plus une source, c'est une conclusion. */}
      {photographie ? (
        <p className="classe__texte muted">Le tableau, photographié par le professeur.</p>
      ) : null}
      {sort.forme === "absentes" ? (
        <p className="classe__texte muted">
          {sort.comptees === 1
            ? "Le professeur a joint une image, sans texte."
            : `Le professeur a joint ${sort.comptees} images, sans texte.`}{" "}
          Elle n'a pas été récupérée ; elle reste lisible sur EcoleDirecte.
        </p>
      ) : null}
      {sort.forme !== "absentes" && !photographie ? (
        <p className="classe__texte">{ligne.content}</p>
      ) : null}
      {sort.forme === "montrees" ? <Images urls={sort.urls} /> : null}
    </li>
  );
}

/**
 * Les photos du tableau, à leur taille, cliquables.
 *
 * Une photo de tableau fait deux mille pixels de large : réduite à la colonne
 * elle est illisible, d'où le lien qui l'ouvre entière. L'URL est signée et
 * expire — la mettre en favori ne donne pas un accès permanent, et c'est voulu
 * (règle 8).
 */
function Images({ urls }: { urls: string[] }) {
  return (
    <ul className="classe__images">
      {urls.map((url, index) => (
        <li key={url}>
          <a href={url} target="_blank" rel="noreferrer">
            <img
              className="classe__image"
              src={url}
              alt={`Image ${index + 1} jointe par le professeur`}
              loading="lazy"
            />
          </a>
        </li>
      ))}
    </ul>
  );
}
